import { type ActStatus, type ActStatusIntent } from '@units/act/types/act.types.js';

/**
 * Все четыре статуса в каноническом порядке — от проекта к отмене.
 *
 * Порядок несущий: он же порядок в отборе реестра и в ключе кеша. Два адреса `?status=REVOKED,DRAFT`
 * и `?status=DRAFT,REVOKED` обязаны дать одну запись кеша, иначе один и тот же ответ хранится
 * дважды, с двумя запросами и двумя моментами устаревания.
 */
export const ACT_STATUSES = ['DRAFT', 'IN_FORCE', 'AMENDED', 'REVOKED'] as const;

/** Пришло ли из адреса то, что реестр умеет отбирать. Незнакомое отбрасывается поштучно. */
export const isActStatus = (value: unknown): value is ActStatus =>
  typeof value === 'string' && (ACT_STATUSES as readonly string[]).includes(value);

/**
 * Статус акта словом, а не оттенком.
 *
 * `rules/a11y.mdc` §2 называет это прямо: отменённый акт, отличающийся от действующего только
 * цветом, — правовая ошибка на выходе, а не косметическая. Поэтому у статуса есть подпись, и
 * подпись — **ключ словаря**, а не фраза: label-map с русским текстом внутри был бы одноязычным
 * навсегда (`test/i18n/label-maps.test.ts` ловит именно это).
 *
 * `Record<ActStatus, string>` — не украшение типа: статус, добавленный в контракт, становится
 * ошибкой компиляции здесь, а не непереведённой строкой на экране.
 */
export const ACT_STATUS_LABEL: Readonly<Record<ActStatus, string>> = {
  DRAFT: 'acts.status.DRAFT',
  IN_FORCE: 'acts.status.IN_FORCE',
  AMENDED: 'acts.status.AMENDED',
  REVOKED: 'acts.status.REVOKED',
};

/**
 * Цвет — **второй** носитель, поверх подписи, и берётся он из палитры темы, а не из литерала.
 *
 * `AMENDED` намеренно не тревожный: «действует в редакции с изменениями» — это живой акт, а не
 * промежуточное состояние (контракт `ActStatus`), и жёлтый читался бы как «что-то не так».
 */
export const ACT_STATUS_COLOR: Readonly<Record<ActStatus, string>> = {
  DRAFT: 'gray',
  IN_FORCE: 'success',
  AMENDED: 'success',
  REVOKED: 'danger',
};

/**
 * Решение человека — два, а не четыре.
 *
 * `PUBLISH` и `REPEAL` охраняются разными правами (`act:publish` и `act:repeal`), и разделение это
 * заказчика: право отменить акт держат не все, кто вправе ввести его в действие.
 */
export const ACT_STATUS_INTENT_LABEL: Readonly<Record<ActStatusIntent, string>> = {
  PUBLISH: 'acts.decision.PUBLISH',
  REPEAL: 'acts.decision.REPEAL',
};

/** Право, которым охраняется каждое из двух намерений (`permissions.catalog.ts`). */
export const ACT_STATUS_INTENT_PERMISSION: Readonly<Record<ActStatusIntent, string>> = {
  PUBLISH: 'act:publish',
  REPEAL: 'act:repeal',
};

/**
 * Цвет кнопки решения — из палитры темы по роли, а не по вкусу.
 *
 * `REPEAL` красный, потому что отмена акта терминальна: восстановление действия оформляется новым
 * актом и новой редакцией, а не откатом статуса. `PUBLISH` — основной цвет продукта: введение в
 * действие обычно и обратимо следующим приказом.
 */
export const ACT_STATUS_INTENT_COLOR: Readonly<Record<ActStatusIntent, string>> = {
  PUBLISH: 'brand',
  REPEAL: 'danger',
};

/** Заголовок подтверждения — свой у каждого намерения: «ввести в действие» и «отменить» не синонимы. */
export const ACT_STATUS_INTENT_DIALOG_TITLE: Readonly<Record<ActStatusIntent, string>> = {
  PUBLISH: 'acts.decision.dialog.PUBLISH',
  REPEAL: 'acts.decision.dialog.REPEAL',
};

/**
 * Что станет правдой после решения — обязательная часть подтверждения юридически значимого
 * действия (`rules/errors-and-toasts.mdc` §14).
 *
 * Диалог без описания последствия — это кнопка «да» без вопроса: человек подтверждает не решение, а
 * своё же нажатие. Для отмены акта последствие выходит за пределы карточки — ассистент перестаёт
 * отвечать по его пунктам, — и сказать об этом надо до нажатия, а не после.
 */
export const ACT_STATUS_INTENT_CONSEQUENCE: Readonly<Record<ActStatusIntent, string>> = {
  PUBLISH: 'acts.decision.consequence.PUBLISH',
  REPEAL: 'acts.decision.consequence.REPEAL',
};
